import { circle } from '@turf/turf';
import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { useTheme } from 'styled-components';

import { SHAPE_FILL_OPACITY } from '../../../constants';
import { useMultiFlightMap } from '../../../MultiFlightMap.context';
import { createTorusRingFeature } from '../../../MultiFlightMap.helpers';

import type { Feature, FeatureCollection, Polygon } from 'geojson';

export function AttributionProximityRadiusLayer() {
  const { attributionZones } = useMultiFlightMap();
  const { uiColors }: any = useTheme();
  const proximityColor = uiColors.multiFlightMapSecondaryColor;

  const features = useMemo<Feature<Polygon>[]>(() => {
    return Object.values(attributionZones)
      .filter(zone => zone.attributionMode === 'footfall' && zone.latlng)
      .flatMap(zone => {
        const center: [number, number] = [zone.latlng!.lng, zone.latlng!.lat];
        const configs = (zone.torusConfig ?? [])
          .filter(config => !config.deleted && typeof config.radius === 'number' && config.radius)
          .sort((a, b) => b.radius! - a.radius!);

        return configs.flatMap((config, index) => {
          const outerRadius = config.radius!;
          const innerRadius = configs[index + 1]?.radius ?? 0;
          if (innerRadius === 0) {
            return [circle(center, outerRadius / 1000, { steps: 64, units: 'kilometers' })];
          }
          const ring = createTorusRingFeature(center, outerRadius, innerRadius, {});
          return ring ? [ring] : [];
        });
      })
      .filter((f): f is Feature<Polygon> => f.geometry?.type === 'Polygon');
  }, [attributionZones]);

  const featureCollection: FeatureCollection<Polygon> = {
    features,
    type: 'FeatureCollection'
  };

  return (
    <Source data={featureCollection} id="attribution-proximity-zones" type="geojson">
      <Layer
        id="attribution-proximity-fill"
        paint={{
          'fill-color': proximityColor,
          'fill-opacity': SHAPE_FILL_OPACITY
        }}
        type="fill"
      />
      <Layer
        id="attribution-proximity-outline"
        paint={{
          'line-color': proximityColor,
          'line-width': 2
        }}
        type="line"
      />
    </Source>
  );
}
